import React, { useEffect, useState } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { Typography, Paper } from '@mui/material'
import { muiOptions, MuiProps, defaultColors, defaultStyles } from './../../infrastructure/materialUiThemeProvider'
import { t } from '../../infrastructure/i18nextHelper'
import { DataTable, ColumnDescriptor, MultipleSelect, GroupedMultiSelect, DateRangePicker, Button } from '../common/customComponents'
import { Guid } from '@guid'
import * as api from '../../infrastructure/api'
import moment from 'moment'
import { convertFromRaw } from 'draft-js'
import { compareAndFixPeriod } from '../../infrastructure/date'
import { setInLocalStorage } from '../../infrastructure/localStorage'
import { ExcelGeneratorContainer } from '../../infrastructure/excelExport'
import { createExcelLines } from '../administration/masterData/masterDataShell'

type CommentFilter = {
    content: string | null
    user: string | null
    createdAt: string | null
    reference: string | null
    site: string | null
    dutyStatus: string | null
    product: string | null
    type: string | null
    company: string | null
}

let emptyColumnFilter: CommentFilter = {
    content: null,
    user: null,
    createdAt: null,
    reference: null,
    site: null,
    dutyStatus: null,
    product: null,
    type: null,
    company: null,
}

type SupplyJournalFilters = {
    startDate: string | null
    endDate: string | null
    objects: string[] | null
    whos: string[] | null
    productIds: Guid[] | null
    sites: string[] | null
    dutyStatuss: string[] | null
    companys: string[] | null
}

type CommentListItem = {
    id: Guid
    associationKey: string
    content: string
    user: string
    createdAt: string
    reference: string
    site: string
    dutyStatus: string
    product: string
    type: string,
    company: string
}

type Site = {
    code: string
    name: string
    siteGroup: string
    dutyStatuses: string[]
}

type Company = {
    code: string
    name: string
    dutyStatuses: string[]
}

type Product = {
    id: Guid
    code: string
    productGroup?: string
}

type CommentObject = {
    code: string,
    name: string
}

type ProfileSelectItem = {
    userName: string,
    fullName: string
}

let defaultFilters: SupplyJournalFilters = {
    startDate: moment.utc().startOf('month').format(),
    endDate: moment.utc().startOf('month').add(1, 'M').add(-1, 'day').format(),
    objects: null,
    whos: null,
    productIds: null,
    sites: null,
    dutyStatuss: null,
    companys: null
}

let initFilters = () => {
    let filters = localStorage.getItem('supplyJournalFilters')
    if (!filters) return defaultFilters
    let filtersObj: SupplyJournalFilters = JSON.parse(filters)
    return {
        ...defaultFilters,
        startDate: filtersObj.startDate,
        endDate: filtersObj.endDate,
        objects: filtersObj.objects,
        whos: filtersObj.whos,
        productIds: filtersObj.productIds,
        sites: filtersObj.sites,
        dutyStatuss: filtersObj.dutyStatuss,
        companys: filtersObj.companys
    }
}

function SupplyJournal({ classes }: MuiProps) {
    let [comments, setComments] = useState<CommentListItem[]>([])
    let [companys, setCompanys] = useState<Company[]>([])
    let [sites, setSites] = useState<Site[]>([])
    let [products, setProducts] = useState<Product[]>([])
    let [dutyStatuss, setDutyStatuss] = useState<string[]>()
    let [profiles, setProfiles] = useState<ProfileSelectItem[]>([])
    let [filters, setFilters] = useState<SupplyJournalFilters>(initFilters())
    let [columnFilter, setColumnFilter] = useState<CommentFilter>(emptyColumnFilter)

    let excelGenerator = ExcelGeneratorContainer.useContainer()

    let objects: CommentObject[] = [
        { code: 'actualStock', name: t('supplyJournal.objectText.actualStock') },
        { code: 'deal', name: t('supplyJournal.objectText.deal') },
        { code: 'dealFinalPricing', name: t('supplyJournal.objectText.dealFinalPricing') },
        { code: 'dealProvisionalPricing', name: t('supplyJournal.objectText.dealProvisionalPricing') },
        { code: 'movement', name: t('supplyJournal.objectText.movement') },
        { code: 'transport', name: t('supplyJournal.objectText.transport') },
        { code: 'vessel', name: t('supplyJournal.objectText.vessel') },
    ]

    useEffect(() => {
        loadReferentials()
    }, [])

    useEffect(() => {
        load()
        saveFiltersInLocalStorage()
    }, [filters])

    let load = async () => {
        var query = constructFiltersQuery(filters)
        let commentItems = await api.get<CommentListItem[]>('comment/search?' + query)
        setComments(commentItems)
    }

    let loadReferentials = async () => {
        let companys = await api.get<Company[]>('comment/company')
        setCompanys(companys)
        let dutyStatuss = companys.map(x => x.dutyStatuses).reduce((a, b) => a.concat(b)).distinct()
        setDutyStatuss(dutyStatuss)

        setSites(await api.get<Site[]>('comment/site'))
        setProducts(await api.get<Product[]>('comment/product'))
        setProfiles(await api.get<ProfileSelectItem[]>('comment/profile'))
    }

    let columns: ColumnDescriptor<CommentListItem>[] = [
        {
            name: t('supplyJournal.company'), value: x => x.company,
            columnFilter: {
                value: columnFilter.company ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, company: val })
            }
        },
        {
            name: t('supplyJournal.when'), value: x => moment(x.createdAt).format('MM/DD/YYYY'),
            columnFilter: {
                value: columnFilter.createdAt ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, createdAt: val })
            }
        },
        {
            name: t('supplyJournal.who'), value: x => x.user,
            columnFilter: {
                value: columnFilter.user ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, user: val })
            }
        },
        {
            name: t('supplyJournal.object'), value: x => objects.find(obj => obj.code === x.type)?.name ?? '',
            columnFilter: {
                value: columnFilter.type ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, type: val })
            }
        },
        {
            name: t('supplyJournal.reference'), value: x => x.reference,
            columnFilter: {
                value: columnFilter.reference ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, reference: val })
            }
        },
        {
            name: t('supplyJournal.site'), value: x => x.site,
            columnFilter: {
                value: columnFilter.site ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, site: val })
            }
        },
        {
            name: t('supplyJournal.dutyStatus'), value: x => x.dutyStatus,
            columnFilter: {
                value: columnFilter.dutyStatus ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, dutyStatus: val })
            }
        },
        {
            name: t('supplyJournal.product'), value: x => x.product,
            columnFilter: {
                value: columnFilter.product ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, product: val })
            }
        },
        {
            name: t('supplyJournal.comment'),
            value: x => getPlainTextContent(x.content),
            columnFilter: {
                value: columnFilter.content ?? '',
                onChange: (val: string) => setColumnFilter({ ...columnFilter, content: val })
            }
        }
    ]

    let safeJsonParse = (input: string) => {
        try {
            return [null, JSON.parse(input)];
        } catch (error) {
            return [error];
        }
    }

    let getPlainTextContent = (content: string) => {
        let [error, jsonContent] = safeJsonParse(content)
        return error
            ? content
            : convertFromRaw(jsonContent).getPlainText()
    }

    let constructFiltersQuery = (filters: SupplyJournalFilters) => {
        let filterProperties = Object.keys(filters)

        let initializedFilters = filterProperties
            .filter(x => !Array.isArray(filters[x]))
            .filter(x => (filters[x] !== undefined && filters[x] !== null))
            .map(x => `${x}=${filters[x]}`)

        let collectionFilters = filterProperties
            .filter(x => Array.isArray(filters[x]))
            .filter(x => filters[x] && filters[x].length > 0)
            .flatMap(x => filters[x].map(value => `${x}=${value}`))

        return initializedFilters.concat(collectionFilters).join('&')
    }

    let changePeriod = (start: string | null, end: string | null) => {
        let supplyJournalfilters: SupplyJournalFilters = { ...filters }
        let newPeriod = compareAndFixPeriod(filters?.startDate, start, filters?.endDate, end)
        supplyJournalfilters.startDate = newPeriod.start
        supplyJournalfilters.endDate = newPeriod.end
        setFilters(supplyJournalfilters)
    }

    let saveFiltersInLocalStorage = () => {
        let filtersLength = Object.keys(filters).length
        if (filtersLength > 0)
            setInLocalStorage('supplyJournalFilters', filters)
    }

    let exportExcel = () => {
        excelGenerator.generate({
            filename: 'SupplyJournal.xlsx',
            sheets: [{ name: 'SupplyJournal', lines: createExcelLines(comments, columns) }]
        })
    }

    let applyFilters = (items: CommentListItem[], filter: CommentFilter): CommentListItem[] => {
        if (filter.content)
            items = items.filter(x => x.content.toLowerCase().contains(filter.content!.toLowerCase()))
        if (filter.user)
            items = items.filter(x => x.user.toLowerCase().contains(filter.user!.toLowerCase()))
        if (filter.createdAt)
            items = items.filter(x => moment(x.createdAt).format('MM/DD/YYYY').contains(filter.createdAt!))
        if (filter.reference)
            items = items.filter(x => x.reference.toLowerCase().contains(filter.reference!.toLowerCase()))
        if (filter.site)
            items = items.filter(x => x.site.toLowerCase().contains(filter.site!.toLowerCase()))
        if (filter.dutyStatus)
            items = items.filter(x => x.dutyStatus.toLowerCase().contains(filter.dutyStatus!.toLowerCase()))
        if (filter.product)
            items = items.filter(x => x.product.toLowerCase().contains(filter.product!.toLowerCase()))
        if (filter.type)
            items = items.filter(x => x.type.toLowerCase().contains(filter.type!.toLowerCase()))
        if (filter.company)
            items = items.filter(x => x.company.toLowerCase().contains(filter.company!.toLowerCase()))

        return items
    }

    let getComments = (): CommentListItem[] =>
        applyFilters(comments, columnFilter)

    return (
        <>
            <div className={classes.pageContainer}>
                <div className={classes.firstRow}>
                    <Typography className={classes.pageTitle} variant='h5' display='block' gutterBottom>{t('supplyJournal.name')}</Typography>
                    <div>
                        <Paper className={classes.paperFilter}>
                            <DateRangePicker disableNewStyle classesOverride={{ datepicker: classes.filterFieldDate }}
                                startLabel={t('supplyJournal.filter.startDate')} startDate={filters.startDate}
                                endLabel={t('supplyJournal.filter.endDate')} endDate={filters.endDate}
                                onPeriodChange={period => changePeriod(period.startDate, period.endDate)} />
                            <MultipleSelect label={t('supplyJournal.filter.who')} classesOverride={{ form: classes.filterField }}
                                values={filters.whos}
                                choices={profiles.map(x => ({ value: x.userName, text: x.fullName }))}
                                onChange={x => setFilters({ ...filters, whos: [...x] })} />
                            <MultipleSelect label={t('supplyJournal.filter.objects')} classesOverride={{ form: classes.filterField }}
                                values={filters.objects}
                                choices={objects.map(x => ({ value: x.code, text: x.name }))}
                                onChange={x => setFilters({ ...filters, objects: [...x] })} />
                            <GroupedMultiSelect label={t('supplyJournal.filter.sites')}
                                classesOverride={{ form: classes.filterField }}
                                values={filters.sites}
                                choices={sites.map(x => ({ value: x.code, text: x.name, group: x.siteGroup }))}
                                allWhenEmpty={false}
                                onChange={x => setFilters({ ...filters, sites: [...x] })} />
                            <GroupedMultiSelect label={t('supplyJournal.filter.products')} classesOverride={{ form: classes.filterField }}
                                values={filters.productIds}
                                choices={products.map(x => ({ value: x.id, text: x.code, group: x.productGroup ?? x.code }))}
                                onChange={x => setFilters({ ...filters, productIds: [...x] })} />
                            <MultipleSelect label={t('supplyJournal.filter.dutyStatuss')} hideOnSingleChoice
                                values={filters.dutyStatuss} classesOverride={{ form: classes.filterField }}
                                choices={dutyStatuss}
                                onChange={x => setFilters({ ...filters, dutyStatuss: [...x] })} />
                            <MultipleSelect label={t('supplyJournal.filter.companys')} classesOverride={{ form: classes.filterField }}
                                values={filters.companys}
                                choices={companys.map(x => ({ value: x.code, text: x.name }))}
                                onChange={(x) => setFilters({ ...filters, companys: [...x] })} />
                        </Paper>
                    </div>
                </div>
                <Paper className={classes.root + ' ' + classes.secondRow}>
                    <div className={classes.tableHead}>
                        <Button onClick={exportExcel} label={t('supplyJournal.export')} img='/static/images/excel_red.svg' className={classes.secondaryButton} />
                    </div>
                    <DataTable
                        items={getComments()}
                        columns={columns}
                        idSelector={(x: CommentListItem) => x.id}
                    />
                </Paper>
            </div>
        </>
    )
}

let styles = theme =>
    createStyles({
        pageContainer: {
            display: 'grid',
            height: '100%',
            paddingBottom: '0.5em',
            'grid-template-areas': "'firstRow' 'secondRow'",
            'grid-template-columns': 'auto',
            'grid-template-rows': '4em minmax(0, 1fr)',
            'grid-gap': '0.8em',
            'overflow-y': 'hidden',
            'box-sizing': 'border-box',
            '&> div': {
                height: '100%',
                width: '100%'
            }
        },
        iconStyle: { height: '1.5rem', width: '1.5rem', marginTop: '-0.2rem' },
        pageTitle: {
            color: defaultColors.grey.dark.color,
            alignSelf: 'flex-start',
            marginRight: '2em'
        },
        firstRow: {
            ...defaultStyles.flexRow,
            justifyContent: 'space-between',
            alignItems: 'stretch',
            width: '100%',
            gridArea: 'firstRow',
            whiteSpace: 'nowrap'
        },
        secondRow: {
            gridArea: 'secondRow'
        },
        root: {
            flexGrow: 2,
            width: '100%',
            overflow: 'auto'
        },
        paperFilter: {
            height: 'auto',
            padding: '0.5em',
        },
        filterField: {
            width: '8.3em',
            margin: '0em 0.5em'
        },
        filterFieldDate: {
            width: '9.3em',
            margin: '0em 0.5em'
        },
        secondaryButton: {
            ...defaultStyles.secondaryButton
        },
        tableHead: {
            ...defaultStyles.flexRow,
            justifyContent: 'end',
            alignItems: 'stretch',
            padding: '0.5em 1%',
            width: '99%'
        },
    })

export default withStyles(styles, muiOptions)(SupplyJournal)